import styled from '@emotion/styled';
import Layout from '@layout/FreeLayout/FreeLayout';
import { mediaQuery, Spacings } from '@styles/index';
import AllCentreHeading from '@cont/AllCentre/AllCentreHeading';
import AllCentreGoals from '@cont/AllCentre/AllCentreGoals';
import AllCentreList from '@cont/AllCentre/AllCentreList';
import AllCentreRanker from '@cont/AllCentre/AllCentreRanker';
import FlagshipCentres from '@cont/AllCentre/FlagshipCentres';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { useEffect, useRef, useState } from 'react';
import Error from '@comp/Error/Error';
import fetch from '@api/fetchers/index';
import PropTypes from 'prop-types';
import { getStaticData } from '@api/fetchers/offline-centres';
import AllCentreEnquiry from '@cont/AllCentre/AllCentreEnquiry';

const LoaderWrapper = styled.div`
  width: 100vw;
  height: 80vh;
  padding-top: ${Spacings.SPACING_30B};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: white;
  ${mediaQuery.uptoMobile} {
    padding-top: ${Spacings.SPACING_16B};
  }
`;

const Wrapper = styled.div`
  width: 100vw;
  padding-top: ${Spacings.SPACING_30B};
  display: flex;
  justify-content: center;
  background-color: white;
  ${mediaQuery.uptoMobile} {
    padding-top: ${Spacings.SPACING_16B};
  }
`;

const AllCentreContainer = styled.div`
  width: 100vw;
  display: flex;
  flex-direction: column;
`;

const AllCentrePage = ({ cmsData }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [allCentreDataJson, setAllCentreDataJson] = useState(null);
  const [allCentreData, setAllCentreData] = useState(null);
  const enquiryFormRef = useRef(null);

  const fetchAllCentreDetails = ({
    lat = null,
    long = null,
    goal_uid = null
  } = {}) => {
    setIsLoading(true);
    const requestBody = {};

    if (lat !== null) {
      requestBody.lat = lat;
    }

    if (long !== null) {
      requestBody.long = long;
    }

    if (goal_uid !== null) {
      requestBody.goal_uid = goal_uid;
    }

    fetch('v1/all-centres/home', {
      requestType: 'POST',
      body: requestBody,
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((currentResponse) => {
        setAllCentreDataJson(currentResponse?.data);
      })
      .catch(() => {
        setAllCentreDataJson(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const userPrevLoc = JSON.parse(localStorage.getItem('userLocation')) || {};
    if (userPrevLoc && userPrevLoc.lat && userPrevLoc.long) {
      fetchAllCentreDetails({
        lat: parseFloat(userPrevLoc.lat),
        long: parseFloat(userPrevLoc.long)
      });
    } else {
      fetchAllCentreDetails();
    }
  }, []);

  useEffect(() => {
    if (allCentreDataJson) {
      const data = allCentreDataJson;

      const centreData = {};
      for (let currIdx = 0; currIdx < data?.length; currIdx += 1) {
        if (data[currIdx]?.componentType === 'hero') {
          centreData.headingData = data[currIdx]?.componentData;
        } else if (data[currIdx]?.componentType === 'goal_selection') {
          centreData.goalData = data[currIdx]?.componentData;
        } else if (data[currIdx]?.componentType === 'top_rankers') {
          centreData.rankerData = data[currIdx]?.componentData;
        } else if (data[currIdx]?.componentType === 'centres_list') {
          centreData.allCentreListData = data[currIdx]?.componentData;
        } else if (data[currIdx]?.componentType === 'centres_near_you') {
          centreData.nearbyCentresData = data[currIdx]?.componentData;
        } else if (data[currIdx]?.componentType === 'flagship_centres') {
          centreData.flagshipCentresData = data[currIdx]?.componentData;
        }
      }

      setAllCentreData(centreData);
    }
  }, [allCentreDataJson]);

  const renderLoaderView = () => {
    return (
      <>
        <LoaderWrapper>
          <CircularLoader
            color="var(--color-i-black)"
            size={Spacings.SPACING_8B}
          />
        </LoaderWrapper>
      </>
    );
  };

  const renderErrorView = () => {
    return (
      <>
        <LoaderWrapper>
          <Error
            title="Something went wrong"
            description="There was an issue connecting with the server. Please try again."
          />
        </LoaderWrapper>
      </>
    );
  };

  if (isLoading) {
    return renderLoaderView();
  }

  if (!isLoading && !allCentreDataJson) {
    return renderErrorView();
  }

  return (
    <>
      <Wrapper>
        <AllCentreContainer>
          {allCentreData?.headingData && (
            <AllCentreHeading
              headingData={allCentreData?.headingData}
              cmsData={cmsData}
              enquiryFormRef={enquiryFormRef}
            />
          )}
          {allCentreData?.goalData && (
            <AllCentreGoals goalData={allCentreData?.goalData} />
          )}
          {allCentreData?.flagshipCentresData && (
            <FlagshipCentres
              flagshipCentresData={allCentreData?.flagshipCentresData}
            />
          )}
          {allCentreData?.allCentreListData && (
            <AllCentreList
              allCenterListData={allCentreData?.allCentreListData}
            />
          )}
          {allCentreData?.rankerData && (
            <AllCentreRanker rankerData={allCentreData?.rankerData} />
          )}
          <AllCentreEnquiry cmsData={cmsData} ref={enquiryFormRef} />
        </AllCentreContainer>
      </Wrapper>
    </>
  );
};

AllCentrePage.pageInfo = {
  layoutOptions: {
    hideGoalSelector: true
  }
};

export const getStaticProps = async () => {
  try {
    const cmsData = await getStaticData();
    return {
      props: {
        cmsData
      },
      revalidate: 60
    };
  } catch (error) {
    return {
      props: {},
      revalidate: 60
    };
  }
};

AllCentrePage.Layout = Layout;

AllCentrePage.Layout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export default AllCentrePage;
AllCentrePage.Wrapper = PageReduxProvider;
